/**
 * Submit driver feedback to the feedback microservice via admin-portal proxy.
 */

// Local CRA uses same-origin /api/* via setupProxy (avoids CORS to :8090).
const FEEDBACK_BASE = (
  process.env.REACT_APP_ENV === 'local'
    ? ''
    : (process.env.REACT_APP_FEEDBACK_URL && String(process.env.REACT_APP_FEEDBACK_URL).trim()) ||
      (process.env.REACT_APP_ADMIN_PORTAL_API_URL && String(process.env.REACT_APP_ADMIN_PORTAL_API_URL).trim()) ||
      'https://220-km.com:8090'
).replace(/\/$/, '');

const FEEDBACK_URL = `${FEEDBACK_BASE}/api/feedback`;
const SERVICE_REQUEST_URL = `${FEEDBACK_BASE}/api/service-request`;
const REFUND_REQUEST_URL = `${FEEDBACK_BASE}/api/refund-request`;

const NEAR_STATION_M = 300;

/**
 * Resize image file to JPEG ≤ maxSide px via canvas.
 * @returns {Promise<Blob>}
 */
export function resizeImageToJpeg(file, maxSide = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('NO_FILE'));
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const scale = Math.min(1, maxSide / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('NO_CANVAS'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('ENCODE_FAILED'));
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('LOAD_FAILED'));
    };
    img.src = url;
  });
}

export function isNearStation(distanceKm) {
  if (distanceKm == null || !Number.isFinite(distanceKm)) return false;
  return distanceKm * 1000 <= NEAR_STATION_M;
}

/**
 * @param {object} params
 * @param {string} params.stationNumber
 * @param {string} params.clientId
 * @param {string} params.text
 * @param {number} params.rating
 * @param {number} params.carMaxKw
 * @param {number|null} params.lat
 * @param {number|null} params.lon
 * @param {number|null} params.stationLat
 * @param {number|null} params.stationLon
 * @param {Blob|File|null} params.photo
 */
export async function submitFeedback({
  stationNumber,
  clientId,
  text,
  rating,
  carMaxKw,
  lat,
  lon,
  stationLat,
  stationLon,
  photo,
}) {
  const form = new FormData();
  form.append('stationNumber', String(stationNumber || ''));
  form.append('text', String(text || ''));
  form.append('rating', String(rating));
  form.append('carMaxKw', String(carMaxKw));
  if (clientId) form.append('clientId', String(clientId));
  if (lat != null && Number.isFinite(lat)) form.append('lat', String(lat));
  if (lon != null && Number.isFinite(lon)) form.append('lon', String(lon));
  if (stationLat != null && Number.isFinite(stationLat)) form.append('stationLat', String(stationLat));
  if (stationLon != null && Number.isFinite(stationLon)) form.append('stationLon', String(stationLon));
  if (photo) {
    form.append('photo', photo, 'car.jpg');
  }

  const res = await fetch(FEEDBACK_URL, {
    method: 'POST',
    body: form,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const detail = data?.detail || data?.error || `HTTP_${res.status}`;
    const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    err.status = res.status;
    err.detail = detail;
    err.retryAfter = res.headers.get('Retry-After');
    throw err;
  }

  return data;
}

/**
 * @param {object} params
 * @param {string} params.stationNumber
 * @param {string} [params.clientId]
 * @param {string|null} [params.city]
 * @param {string[]} params.problemTypes
 * @param {string} params.description
 * @param {string} params.phone
 * @param {string|null} [params.carInfo]
 * @param {Blob|File|null} [params.photoCarPort]
 * @param {Blob|File|null} [params.photoCar]
 * @param {Blob|File|null} [params.photoStation]
 */
export async function submitServiceRequest({
  stationNumber,
  clientId,
  city,
  problemTypes,
  description,
  phone,
  carInfo,
  photoCarPort,
  photoCar,
  photoStation,
}) {
  const form = new FormData();
  form.append('stationNumber', String(stationNumber || ''));
  form.append('description', String(description || ''));
  form.append('phone', String(phone || ''));
  (problemTypes || []).forEach(pt => form.append('problemTypes', String(pt)));
  if (clientId) form.append('clientId', String(clientId));
  if (city) form.append('city', String(city));
  if (carInfo) form.append('carInfo', String(carInfo));
  if (photoCarPort) form.append('photoCarPort', photoCarPort, 'car_port.jpg');
  if (photoCar) form.append('photoCar', photoCar, 'car.jpg');
  if (photoStation) form.append('photoStation', photoStation, 'station.jpg');

  const res = await fetch(SERVICE_REQUEST_URL, {
    method: 'POST',
    body: form,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const detail = data?.detail || data?.error || `HTTP_${res.status}`;
    const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    err.status = res.status;
    err.detail = detail;
    err.retryAfter = res.headers.get('Retry-After');
    throw err;
  }

  return data;
}

/**
 * @param {object} params
 * @param {string} params.stationNumber
 * @param {string|null} [params.invoiceId] — omit for new-phone / no local session
 * @param {string|null} [params.jobId]
 * @param {string} [params.clientId]
 * @param {string} params.phone
 * @param {string} params.fullName
 * @param {string[]} [params.problemTypes]
 * @param {string} [params.reason] — legacy single value; prefer problemTypes
 * @param {string} params.description
 * @param {string} params.cardNumber
 * @param {string|number} params.amountUah
 * @param {boolean} params.contactedSupport
 * @param {number} params.chargingRating
 * @param {Blob|File} params.photoReceipt
 * @param {Blob|File} params.photoCar
 */
export async function submitRefundRequest({
  stationNumber,
  invoiceId,
  jobId,
  clientId,
  phone,
  fullName,
  problemTypes,
  reason,
  description,
  cardNumber,
  amountUah,
  contactedSupport,
  chargingRating,
  photoReceipt,
  photoCar,
}) {
  const form = new FormData();
  form.append('stationNumber', String(stationNumber || ''));
  form.append('phone', String(phone || ''));
  form.append('fullName', String(fullName || ''));
  const types = Array.isArray(problemTypes) ? problemTypes.filter(Boolean) : [];
  if (types.length > 0) {
    types.forEach(pt => form.append('problemTypes', String(pt)));
  } else if (reason) {
    form.append('reason', String(reason));
  }
  form.append('description', String(description || ''));
  form.append('cardNumber', String(cardNumber || ''));
  form.append('amountUah', String(amountUah ?? ''));
  form.append('contactedSupport', contactedSupport ? 'true' : 'false');
  form.append('chargingRating', String(chargingRating));
  if (invoiceId) form.append('invoiceId', String(invoiceId));
  if (jobId) form.append('jobId', String(jobId));
  if (clientId) form.append('clientId', String(clientId));
  if (photoReceipt) form.append('photoReceipt', photoReceipt, 'receipt.jpg');
  if (photoCar) form.append('photoCar', photoCar, 'car.jpg');

  const res = await fetch(REFUND_REQUEST_URL, {
    method: 'POST',
    body: form,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const detail = data?.detail || data?.error || `HTTP_${res.status}`;
    const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    err.status = res.status;
    err.detail = detail;
    err.retryAfter = res.headers.get('Retry-After');
    throw err;
  }

  return data;
}

export const PUBLIC_REPORTS_PAGE_SIZE = 3;

function stationSearch(stationNumbers, page) {
  const params = new URLSearchParams();
  (stationNumbers || []).forEach(value => {
    const station = String(value ?? '').trim();
    if (station) params.append('stations', station);
  });
  params.set('page', String(page || 1));
  return params;
}

async function readPublicList(res) {
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok || !data || !Array.isArray(data.items)) {
    const err = new Error(data?.detail || `HTTP_${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

/**
 * Public reviews for the given station numbers. Page size is fixed at 3.
 * @param {{ stationNumbers: Array<string|number>, page?: number }} params
 */
export async function listFeedback({ stationNumbers, page = 1 }) {
  const params = stationSearch(stationNumbers, page);
  if (!params.get('stations')) {
    return { items: [], total: 0, page: 1, page_size: PUBLIC_REPORTS_PAGE_SIZE };
  }
  const res = await fetch(`${FEEDBACK_URL}?${params.toString()}`);
  return readPublicList(res);
}

/**
 * Public service requests for the given station numbers. Phone is not returned.
 * @param {{ stationNumbers: Array<string|number>, page?: number }} params
 */
export async function listServiceRequests({ stationNumbers, page = 1 }) {
  const params = stationSearch(stationNumbers, page);
  if (!params.get('stations')) {
    return { items: [], total: 0, page: 1, page_size: PUBLIC_REPORTS_PAGE_SIZE };
  }
  const res = await fetch(`${SERVICE_REQUEST_URL}?${params.toString()}`);
  return readPublicList(res);
}

export function feedbackPhotoUrl(id, { thumb = false } = {}) {
  return `${FEEDBACK_URL}/${id}/photo${thumb ? '?thumb=1' : ''}`;
}

export function serviceRequestPhotoUrl(id, kind, { thumb = false } = {}) {
  return `${SERVICE_REQUEST_URL}/${id}/photo/${encodeURIComponent(kind)}${thumb ? '?thumb=1' : ''}`;
}

export { NEAR_STATION_M, FEEDBACK_URL, SERVICE_REQUEST_URL, REFUND_REQUEST_URL };
